import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '@/contexts/ChatContext';
import { useAuth } from '@/components/AuthProvider';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Search,
  MessageCircle,
  UserPlus
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface TeamMembersListProps {
  selectedMemberId?: string | null;
  onSelectMember?: (memberId: string) => void;
  onSearchUser?: () => void;
  className?: string;
}

export function TeamMembersList({ selectedMemberId, onSelectMember, onSearchUser, className }: TeamMembersListProps) {
  const { teamMembers, unreadCounts, lastMessages, isConnected, isConnecting, connect, isLoadingMembers } = useChat();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [internalSelected, setInternalSelected] = useState<string | null>(null);
  
  const currentSelected = selectedMemberId !== undefined ? selectedMemberId : internalSelected;

  // Auto-connect so org users are available before any chat is started
  useEffect(() => {
    if (!user?.id) return;
    if (isConnected || isConnecting) return;
    connect();
  }, [user?.id, isConnected, isConnecting, connect]);

  // Snapshot the sort order once when teamMembers first load, so the list
  // doesn't jump around when lastMessages updates during the session.
  const initialSortRef = useRef<Record<string, number>>({});
  const hasSortedRef = useRef(false);

  if (teamMembers.length > 0 && !hasSortedRef.current) {
    const sorted = [...teamMembers]
      .filter(m => m.id !== user?.id)
      .sort((a, b) => {
        const aTime = lastMessages[a.id]?.created_at ? new Date(lastMessages[a.id].created_at).getTime() : 0;
        const bTime = lastMessages[b.id]?.created_at ? new Date(lastMessages[b.id].created_at).getTime() : 0;
        if (aTime !== bTime) return bTime - aTime;
        if (a.is_online !== b.is_online) return a.is_online ? -1 : 1;
        return (a.full_name || a.email).localeCompare(b.full_name || b.email);
      });
    sorted.forEach((m, i) => { initialSortRef.current[m.id] = i; });
    hasSortedRef.current = true;
  }

  // Filter by search, sort by the stable initial order
  const filteredMembers = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    return teamMembers
      .filter(member =>
        member.id !== user?.id &&
        (member.full_name?.toLowerCase().includes(lowerSearch) ||
          member.email.toLowerCase().includes(lowerSearch))
      )
      .sort((a, b) => {
        const aIdx = initialSortRef.current[a.id] ?? 9999;
        const bIdx = initialSortRef.current[b.id] ?? 9999;
        return aIdx - bIdx;
      });
  }, [teamMembers, searchTerm, user?.id]);

  const handleMemberClick = (memberId: string) => {
    setInternalSelected(memberId);
    onSelectMember?.(memberId);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

const getStatusText = (isOnline: boolean, lastSeen: string) => {
    if (isOnline) return 'Online';

    const lastSeenDate = new Date(lastSeen);
    return `Last seen ${formatDistanceToNow(lastSeenDate, { addSuffix: true })}`;
  };

  return (
    <div className={`flex flex-col h-full bg-background ${className}`} style={{ height: '100%' }}>
      {/* Header */}
      <div className="p-4 border-b bg-muted/30">
        <h3 className="font-semibold text-lg mb-4">Team Members</h3>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search members..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-background"
          />
        </div>
      </div>

      {/* Members List */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-2 space-y-1">
          {filteredMembers.map((member) => (
            <div
              key={member.id}
              className={`relative flex items-center gap-3 p-3 pr-12 rounded-lg cursor-pointer transition-all duration-200 hover:bg-muted/50 hover:shadow-sm ${currentSelected === member.id ? 'bg-muted shadow-sm border border-border/50' : ''
                }`}
              onClick={() => handleMemberClick(member.id)}
            >
              <div className="relative">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={member.avatar_url} />
                  <AvatarFallback>
                    {getInitials(member.full_name || member.email)}
                  </AvatarFallback>
                </Avatar>
                <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background ${member.is_online ? 'bg-green-500' : 'bg-gray-300'}`} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="font-medium text-sm truncate min-w-0 flex-1">
                    {member.full_name || member.email}
                  </p>
                </div>

                <div className="flex items-center gap-2 min-w-0 mt-0.5">
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 flex-shrink-0">
                    {member.role}
                  </Badge>

                  {/* Last message preview if available, otherwise online status */}
                  {lastMessages[member.id] ? (
                    <p className={`text-xs truncate min-w-0 flex-1 ${(unreadCounts[member.id] || 0) > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                      {lastMessages[member.id].sender_id === user?.id ? 'You: ' : ''}
                      {lastMessages[member.id].content.length > 45
                        ? lastMessages[member.id].content.substring(0, 45) + '…'
                        : lastMessages[member.id].content}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground truncate min-w-0 flex-1">
                      {getStatusText(member.is_online, member.last_seen)}
                    </p>
                  )}
                </div>
              </div>

              {/* Unread badge (always visible) */}
              {(unreadCounts[member.id] || 0) > 0 && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <Badge className="text-xs rounded-full min-w-[20px] flex items-center justify-center bg-emerald-500 text-white hover:bg-emerald-600">
                    {unreadCounts[member.id] > 99 ? '99+' : unreadCounts[member.id]}
                  </Badge>
                </div>
              )}
            </div>
          ))}

          {filteredMembers.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                <MessageCircle className="h-8 w-8 text-muted-foreground" />
              </div>

              {searchTerm ? (
                <div className="space-y-2">
                  <h4 className="font-medium text-foreground">No results found</h4>
                  <p className="text-sm text-muted-foreground">
                    Try adjusting your search terms
                  </p>
                </div>
              ) : (
                <div className="space-y-4 max-w-xs">
                  <div className="space-y-2">
                    <h4 className="font-medium text-foreground">
                      {isLoadingMembers ? 'Loading members…' : 'No team members'}
                    </h4>
                    {!isLoadingMembers && (
                      <p className="text-sm text-muted-foreground">
                        Start a conversation by finding a user in your organization
                      </p>
                    )}
                  </div>

                  {onSearchUser && (
                    <Button
                      onClick={onSearchUser}
                      size="sm"
                      className="w-full flex items-center justify-center gap-2"
                    >
                      <UserPlus className="h-4 w-4" />
                      Find User
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
